/**
 * mainApi — Current project's main backend (e.g. hammerhead / mlist).
 * Uses REACT_APP_API_BASE_URL and Bearer token auth. Used by TheMList, auth flows, etc.
 * Do not confuse with hoodieApi (src/api), which talks to the reference Django backend.
 */
import axios from 'axios';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || 'https://hammerhead-app-prw98.ondigitalocean.app/', // Fallback to localhost if env is missing
});

// Function to refresh token
const refreshToken = async () => {
    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (!storedRefreshToken) {
        throw new Error('No refresh token available');
    }

    const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL || 'https://hammerhead-app-prw98.ondigitalocean.app/'}/token/refresh/`, {
        refresh: storedRefreshToken,
    });

    localStorage.setItem('accessToken', response.data.access);
    return response.data.access;
};

// Request interceptor to add access token in headers
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor to handle token expiration, network errors, etc.
api.interceptors.response.use(
    (response) => response, // On success, return the response
    async (error) => {
        // Handle network errors like ERR_CONNECTION_REFUSED
        if (error.message && error.message.includes('ERR_CONNECTION_REFUSED')) {
            console.error('Network error: Connection refused. Redirecting to login.');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            window.location.href = '/login';
            return;
        }

        // Handle unauthorized errors (401) and attempt token refresh
        if (error.response && error.response.status === 401) {
            try {
                const newToken = await refreshToken();
                error.config.headers['Authorization'] = `Bearer ${newToken}`;
                return api(error.config); // Retry the original request
            } catch (refreshError) {
                console.error('Token refresh failed. Logging out.');
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                window.location.href = '/login';
            }
        }

        // Log the error and reject the promise for other errors
        console.error('API error:', error);
        return Promise.reject(error);
    }
);

// Fetch mlist listings
export const getMlistListings = async () => {
  try {
    const response = await api.get(`/mlist/all-listings/`);  // Ensure the endpoint is correct
    if (response && response.data) {
      return response.data;  // Ensure that `response.data` contains the user data
    } else {
      console.warn('No data returned from API');
      return [];  // Return an empty array in case of no data
    }
  } catch (error) {
    console.error('Error fetching Mlist listings:', error);
    throw error;  // Re-throw the error if needed to handle it higher up
  }
};


export default api;